import React from 'react';
import { startCase } from 'lodash';
import { View, Field, Table, Text, Card, Page } from '../../components';
import {
  formatCurrencyOnBlur,
  aggregateProjects,
  paginateArray,
  limitString,
} from '../../../../helpers/utils';
import { Theme } from '../../../../theme';

interface Props {
  data: any[];
  meta: any;
}

const Summary = ({ data: clients, meta }: Props) => {
  const paginatedData = paginateArray(clients);
  const { hours, projects, ...statuses } = aggregateProjects(clients) as any;
  const aggregateData = Object.entries(
    statuses as Record<string, { count: any; amount: any }>,
  ).map(([status, values]) => ({
    status: startCase(status),
    ...values,
  }));

  const aggregateColumns = [
    {
      Header: 'Status',
      accessor: 'status',
    },
    {
      Header: 'Count',
      accessor: 'count',
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ cell: { value } }) =>
        value !== 0 ? formatCurrencyOnBlur(value.toString()) : '-',
    },
  ];

  const projectColumns = [
    {
      Header: 'Client',
      accessor: 'name',
    },
    {
      Header: 'Funder',
      accessor: 'funder_name',
      Cell: ({ cell: { value } }) => value || '-',
      id: 'project_funder.name',
    },
    {
      Header: 'Project',
      accessor: 'description',
      id: 'project_description',
      width: 150,
      Cell: ({ cell: { value } }) => <Text>{limitString(value, 150)}</Text>,
    },
    {
      Header: 'Amount',
      accessor: 'amount_requested',
      id: 'project_amount_requested',
      Cell: ({
        cell: { value },
        row: {
          original: { amount_awarded, status },
        },
      }) => {
        let amount = value;
        if (status === 'Awarded') {
          amount = amount_awarded;
        }
        return <Text>{formatCurrencyOnBlur(amount)}</Text>;
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      id: 'project_status',
    },
    {
      Header: 'Due Date',
      accessor: 'deadline',
      id: 'project_deadline',
    },
    {
      Header: 'Hours',
      accessor: 'actual_hours',
      id: 'project_actual_hours',
      Cell: ({
        cell: { value },
        row: {
          original: { actual_hours, estimated_hours },
        },
      }) => {
        let projectHours = value;
        if (actual_hours >= 1 || actual_hours != null) {
          projectHours = actual_hours;
        } else {
          projectHours = estimated_hours;
        }
        return <Text>{projectHours}</Text>;
      },
    },
  ];

  const clientNames = [...new Set(clients.map(({ name }) => name))].join('; ');

  return (
    <>
      <Page meta={meta}>
        <View pb="15pt">
          <View
            mb={1}
            borderBottom={`1pt solid ${Theme.colors.primary}`}
            width="100%"
          >
            <Text fontSize="14pt">Aggregates</Text>
          </View>
          <View flexGrow={1}>
            <Field label="Clients" value={clientNames} maxWidth="50%" />
            <Field label="Total Projects" value={projects.count} />
            <Field
              label="Total Amount"
              value={
                projects.amount !== 0
                  ? formatCurrencyOnBlur(projects.amount.toString())
                  : 'No Recorded Info'
              }
            />
            <Field label="Total Hours" value={hours.toFixed(2)} />
          </View>
          <Card width="50%" flexGrow={1} alignSelf="center">
            <Table
              data={aggregateData}
              columns={aggregateColumns}
              name="aggregate"
            />
          </Card>
        </View>
      </Page>
      {paginatedData.map((data, i) => (
        <Page key={`page_${i + 1}`} meta={meta}>
          <View>
            <View
              pb={1}
              mb={3}
              borderBottom={`1pt solid ${Theme.colors.primary}`}
              width="100%"
            >
              <Text fontSize="14pt">Projects</Text>
            </View>
            <Table data={data} columns={projectColumns} name="project" />
          </View>
        </Page>
      ))}
    </>
  );
};

export default Summary;
